/**
 * Date & Time Formatting Utilities
 * Centralized date formatting for admin interface with user preferences support
 */

/**
 * Format a date/time according to admin preferences
 * @param {Date|Timestamp|string} timestamp - Firestore timestamp, Date object, or ISO string
 * @param {Object} preferences - Admin preferences object with dateFormat, timeFormat, timezone, showTimezones
 * @param {Object} options - Additional formatting options { showTime: boolean, showTimezone: boolean }
 * @returns {string} Formatted date string
 */
export function formatAdminDate(timestamp, preferences, options = {}) {
  if (!timestamp) return '--';

  try {
    // Convert Firestore Timestamp to Date
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) return '--';

    // Convert to user's preferred timezone (basic implementation for now)
    const zonedDate = convertToTimezone(date, preferences?.timezone || 'UTC');

    const format = preferences?.dateFormat || 'us';
    const showTime = options.showTime !== false;
    const showTimezone = options.showTimezone !== undefined
      ? options.showTimezone
      : (preferences?.showTimezones !== false);

    switch (format) {
      case 'us':
        return formatUSDate(zonedDate, preferences, { showTime, showTimezone });
      case 'european':
        return formatEuropeanDate(zonedDate, preferences, { showTime, showTimezone });
      case 'iso':
        return formatISODate(zonedDate, preferences, { showTime, showTimezone });
      default:
        return formatUSDate(zonedDate, preferences, { showTime, showTimezone });
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return '--';
  }
}

/**
 * Get timezone abbreviation from Date object
 * @param {Date} date - Date object
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/Toronto')
 * @returns {string} Timezone abbreviation (e.g., 'EST')
 */
function getTimezoneAbbreviation(date, timezone) {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });

    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    return timeZonePart ? timeZonePart.value : timezone;
  } catch (error) {
    console.warn('Failed to get timezone abbreviation:', error);
    return timezone; // Fallback to IANA identifier
  }
}

/**
 * Format date in US style: "Jan 13, 2026, 4:28 PM"
 */
function formatUSDate(date, preferences, options) {
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();

  let formatted = `${month} ${day}, ${year}`;

  if (options.showTime) {
    const time = formatTime(date, preferences?.timeFormat || '12h');
    formatted += `, ${time}`;
  }

  if (options.showTimezone) {
    const tz = preferences?.timezone || 'UTC';
    const abbr = getTimezoneAbbreviation(date, tz);
    formatted += ` ${abbr}`;
  }

  return formatted;
}

/**
 * Format date in European style: "13 Jan 2026, 16:28"
 */
function formatEuropeanDate(date, preferences, options) {
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();

  let formatted = `${day} ${month} ${year}`;

  if (options.showTime) {
    const time = formatTime(date, preferences?.timeFormat || '24h');
    formatted += `, ${time}`;
  }

  if (options.showTimezone) {
    const tz = preferences?.timezone || 'UTC';
    const abbr = getTimezoneAbbreviation(date, tz);
    formatted += ` ${abbr}`;
  }

  return formatted;
}

/**
 * Format date in ISO style: "2026-01-13 16:28"
 */
function formatISODate(date, preferences, options) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  let formatted = `${year}-${month}-${day}`;

  if (options.showTime) {
    const time = formatTime(date, preferences?.timeFormat || '24h');
    formatted += ` ${time}`;
  }

  if (options.showTimezone) {
    const tz = preferences?.timezone || 'UTC';
    const abbr = getTimezoneAbbreviation(date, tz);
    formatted += ` ${abbr}`;
  }

  return formatted;
}

/**
 * Format time in 12-hour or 24-hour format
 * @param {Date} date - Date object
 * @param {string} format - '12h' or '24h'
 * @returns {string} Formatted time string
 */
function formatTime(date, format) {
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');

  if (format === '12h') {
    const displayHours = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${displayHours}:${minutes} ${ampm}`;
  } else {
    // 24h format
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }
}

/**
 * Convert date to specific timezone
 * @param {Date} date - Date object (in UTC)
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York', 'UTC')
 * @returns {Date} Date object adjusted to timezone
 */
function convertToTimezone(date, timezone) {
  if (!timezone || timezone === 'UTC') {
    return date;
  }

  try {
    // Get the date/time components in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(date);
    const getValue = (type) => parts.find(p => p.type === type)?.value;

    // Reconstruct date in the target timezone
    const year = parseInt(getValue('year'));
    const month = parseInt(getValue('month')) - 1;
    const day = parseInt(getValue('day'));
    const hour = parseInt(getValue('hour'));
    const minute = parseInt(getValue('minute'));
    const second = parseInt(getValue('second'));

    // Create new date (will be interpreted as local time)
    return new Date(year, month, day, hour, minute, second);
  } catch (error) {
    console.warn('Timezone conversion failed, returning original date:', error);
    return date;
  }
}

/**
 * Get list of supported timezones
 * @returns {Array<{value: string, label: string}>} Array of timezone options
 */
export function getTimezoneOptions() {
  return [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'EST (Eastern Time)' },
    { value: 'America/Chicago', label: 'CST (Central Time)' },
    { value: 'America/Denver', label: 'MST (Mountain Time)' },
    { value: 'America/Los_Angeles', label: 'PST (Pacific Time)' },
    { value: 'America/Toronto', label: 'Toronto (Eastern)' },
    { value: 'America/Vancouver', label: 'Vancouver (Pacific)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' }
  ];
}

/**
 * Get default admin preferences
 * @returns {Object} Default preferences object
 */
export function getDefaultPreferences() {
  return {
    dateFormat: 'us',        // 'us' | 'european' | 'iso'
    timeFormat: '12h',       // '12h' | '24h'
    timezone: 'UTC',         // IANA timezone identifier
    showTimezones: true,     // Show timezone suffix
    locale: 'en-US'          // Locale identifier
  };
}

/**
 * Local calendar day as 'YYYY-MM-DD' (baker's timezone), not the UTC day.
 * Use instead of date.toISOString().split('T')[0], which shifts the day
 * (e.g. France: a clicked day becomes the previous day; Canada: after ~8 pm "today" becomes tomorrow).
 * @param {Date|Timestamp|string} value - Date, Firestore Timestamp, ISO string, or 'YYYY-MM-DD' (returned as-is)
 * @returns {string|null} 'YYYY-MM-DD', or null when invalid
 */
export function toLocalDateString(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = value?.toDate ? value.toDate() : (value instanceof Date ? value : new Date(value));
  if (isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Read a 'YYYY-MM-DD' date as a LOCAL date (noon, to stay clear of DST changes).
 * new Date('YYYY-MM-DD') is UTC midnight, which is the previous day in the Americas.
 * Anything else (ISO timestamp, Date, Firestore Timestamp) is converted as-is.
 * @returns {Date|null}
 */
export function parseLocalDate(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  }
  const date = value?.toDate ? value.toDate() : (value instanceof Date ? value : new Date(value));
  return isNaN(date.getTime()) ? null : date;
}

/** Local 00:00:00.000 of the given day ('YYYY-MM-DD', Date or timestamp). */
export function startOfLocalDay(value) {
  const date = parseLocalDate(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

/** Local 23:59:59.999 of the given day — use for the END of a date range so the whole day is included. */
export function endOfLocalDay(value) {
  const date = parseLocalDate(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

/** Whole days from today (local) to the given day: 0 = today, 1 = tomorrow, -1 = yesterday. */
export function daysFromToday(value) {
  const day = startOfLocalDay(value);
  if (!day) return null;
  const today = startOfLocalDay(new Date());
  return Math.round((day - today) / 86400000);
}
